export type ReadingEffectInput = {
  attribute: string;
  document: Document;
  value: string;
  variable: string;
};

export type ResetReadingEffectInput = Omit<ReadingEffectInput, "value">;

export type ReadingGuideMode = "center" | "pointer";

export type ApplyReadingGuideInput = {
  document: Document;
  height: number;
  mode: ReadingGuideMode;
  requestAnimationFrame?: (callback: () => void) => number;
};

const styleSelector = 'style[data-migrosone-a11y-page-effects="reading"]';
const guideSelector = '[data-migrosone-a11y-reading-guide="true"]';
const pageScope = "body > :not(#migrosone-a11y-widget-root)";
const readingGuideZIndex = 2147482000;

const readingEffectStyles = `
html[data-migrosone-a11y-reading-effects][data-migrosone-a11y-reading-mode] ${pageScope} {
  box-sizing: border-box;
  max-width: min(var(--migrosone-a11y-reading-max-width, 68ch), calc(100vw - 32px));
  margin-inline: auto;
  padding-inline: var(--migrosone-a11y-reading-padding-inline, 16px);
}

html[data-migrosone-a11y-reading-effects][data-migrosone-a11y-reading-mode] ${pageScope} > * {
  max-width: 100%;
}

[data-migrosone-a11y-reading-guide="true"] {
  position: fixed;
  left: 0;
  right: 0;
  top: var(--migrosone-a11y-reading-guide-top, 50vh);
  z-index: ${readingGuideZIndex};
  box-sizing: border-box;
  height: var(--migrosone-a11y-reading-guide-height, 48px);
  margin-top: calc(var(--migrosone-a11y-reading-guide-height, 48px) / -2);
  pointer-events: none;
  border-block: 2px solid rgb(31 111 74 / 70%);
  background: rgb(247 201 72 / 22%);
}
`;

type GuideState = {
  listener?: (event: MouseEvent) => void;
};

const guideStates = new WeakMap<Document, GuideState>();

function canUseDocument(document: Document) {
  return Boolean(document.head && document.body && document.documentElement);
}

function ensureReadingEffectStyle(document: Document) {
  const existing = document.querySelector(styleSelector);
  if (existing) {
    return true;
  }

  const style = document.createElement("style");
  style.setAttribute("data-migrosone-a11y-page-effects", "reading");
  style.textContent = readingEffectStyles;
  document.head.appendChild(style);
  return true;
}

function findGuideElement(document: Document) {
  return document.querySelector(guideSelector);
}

export function applyReadingEffectValue({
  attribute,
  document,
  value,
  variable,
}: ReadingEffectInput) {
  if (!canUseDocument(document)) {
    return false;
  }

  ensureReadingEffectStyle(document);
  document.documentElement.style.setProperty(variable, value);
  document.documentElement.setAttribute(attribute, "true");
  document.documentElement.setAttribute("data-migrosone-a11y-reading-effects", "true");
  return true;
}

export function resetReadingEffectValue({
  attribute,
  document,
  variable,
}: ResetReadingEffectInput) {
  if (!canUseDocument(document)) {
    return false;
  }

  document.documentElement.style.removeProperty(variable);
  document.documentElement.removeAttribute(attribute);
  return true;
}

export function applyReadingGuideEffect({
  document,
  height,
  mode,
  requestAnimationFrame: requestFrame,
}: ApplyReadingGuideInput) {
  if (!canUseDocument(document)) {
    return false;
  }

  resetReadingGuideEffect({ document });
  ensureReadingEffectStyle(document);

  const guide = document.createElement("div");
  guide.setAttribute("aria-hidden", "true");
  guide.setAttribute("data-migrosone-a11y-reading-guide", "true");
  guide.setAttribute("data-migrosone-a11y-reading-guide-mode", mode);
  document.body.appendChild(guide);

  document.documentElement.style.setProperty(
    "--migrosone-a11y-reading-guide-height",
    `${height}px`,
  );
  document.documentElement.style.setProperty(
    "--migrosone-a11y-reading-guide-top",
    mode === "center" ? "50vh" : "50vh",
  );
  document.documentElement.setAttribute(
    "data-migrosone-a11y-reading-guide-active",
    "true",
  );
  document.documentElement.setAttribute("data-migrosone-a11y-reading-effects", "true");

  if (mode === "pointer") {
    let scheduled = false;
    let nextTop = "50vh";
    const scheduleFrame =
      requestFrame ??
      globalThis.requestAnimationFrame?.bind(globalThis) ??
      ((callback: () => void) => {
        callback();
        return 0;
      });
    const listener = (event: MouseEvent) => {
      nextTop = `${Math.max(0, event.clientY)}px`;
      if (scheduled) {
        return;
      }

      scheduled = true;
      scheduleFrame(() => {
        scheduled = false;
        document.documentElement.style.setProperty(
          "--migrosone-a11y-reading-guide-top",
          nextTop,
        );
      });
    };

    document.addEventListener("mousemove", listener, { passive: true });
    guideStates.set(document, { listener });
  } else {
    guideStates.set(document, {});
  }

  return true;
}

export function resetReadingGuideEffect({ document }: { document: Document }) {
  if (!canUseDocument(document)) {
    return false;
  }

  const state = guideStates.get(document);
  if (state?.listener) {
    document.removeEventListener("mousemove", state.listener);
  }
  guideStates.delete(document);

  findGuideElement(document)?.remove();
  document.documentElement.style.removeProperty(
    "--migrosone-a11y-reading-guide-height",
  );
  document.documentElement.style.removeProperty("--migrosone-a11y-reading-guide-top");
  document.documentElement.removeAttribute(
    "data-migrosone-a11y-reading-guide-active",
  );
  return true;
}
